/**
 * WebSocket client with auto-reconnect, heartbeat, and channel subscription.
 *
 * Connects using a JWT token in the query string, supports exponential
 * backoff for reconnection, and provides a message callback API.
 */

export type WsMessageHandler = (data: Record<string, unknown>) => void;

export interface WsClientOptions {
  /** Base WebSocket URL (e.g. ws://localhost:8000). Defaults to window location. */
  baseUrl?: string;
  /** JWT access token appended as ?token=<jwt>. */
  token: string;
  /** Callback fired for every incoming JSON message. */
  onMessage?: WsMessageHandler;
  /** Callback fired when connection status changes. */
  onStatusChange?: (status: WsConnectionStatus) => void;
}

export type WsConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

const MIN_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 30_000;

export class WsClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private intentionallyClosed = false;
  private status: WsConnectionStatus = 'disconnected';

  private url: string;
  private onMessage: WsMessageHandler | undefined;
  private onStatusChange: ((status: WsConnectionStatus) => void) | undefined;

  constructor(
    /** Relative path, e.g. /ws/notifications/ */
    path: string,
    options: WsClientOptions,
  ) {
    const base =
      options.baseUrl ??
      `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    this.url = `${base}${path}?token=${encodeURIComponent(options.token)}`;
    this.onMessage = options.onMessage;
    this.onStatusChange = options.onStatusChange;
  }

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  connect(): void {
    this.intentionallyClosed = false;
    this.setStatus('connecting');
    this.createSocket();
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.setStatus('disconnected');
  }

  send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  getStatus(): WsConnectionStatus {
    return this.status;
  }

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  private createSocket(): void {
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setStatus('error');
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.setStatus('connected');
      this.startHeartbeat();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        this.onMessage?.(data);
      } catch {
        // Non-JSON message (e.g. pong) -- ignore.
      }
    };

    this.ws.onclose = () => {
      this.stopHeartbeat();
      if (!this.intentionallyClosed) {
        this.setStatus('reconnecting');
        this.scheduleReconnect();
      } else {
        this.setStatus('disconnected');
      }
    };

    this.ws.onerror = () => {
      // onclose will fire after onerror, which handles reconnection.
      this.setStatus('error');
    };
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed) return;

    const delay = Math.min(
      MIN_RECONNECT_MS * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_MS,
    );
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.setStatus('reconnecting');
      this.createSocket();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setStatus(newStatus: WsConnectionStatus): void {
    if (this.status !== newStatus) {
      this.status = newStatus;
      this.onStatusChange?.(newStatus);
    }
  }
}
